'use client'

import React from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover'
import { Calendar as CalendarIcon } from 'lucide-react'

type Props = {
  visible: boolean

  firstName: string
  middleName: string
  lastName: string
  setFirstName: (v: string) => void
  setMiddleName: (v: string) => void
  setLastName: (v: string) => void

  dirtyFirst: boolean
  setDirtyFirst: (v: boolean) => void
  dirtyLast: boolean
  setDirtyLast: (v: boolean) => void
  dirtyEmail: boolean
  setDirtyEmail: (v: boolean) => void

  validFirst: boolean
  validLast: boolean
  validEmail: boolean

  dob: Date | null | undefined
  setDob: (d: Date | null) => void
  minDob: Date
  maxDob: Date
  dobOpen: boolean
  setDobOpen: (v: boolean) => void

  gender: string
  setGender: (v: string) => void

  email: string
  setEmail: (v: string) => void

  calculateAge: (d: Date) => string
  CustomCalendar: React.ComponentType<{
    className?: string
    selected: Date | null
    onChange: (d: Date | null) => void
    minDate: Date
    maxDate: Date
  }>

  // NEW: when true, show a single "Name" field instead of first/middle/last
  singleNameField?: boolean
}

const PatientDetailsForm = (props: Props) => {
  const {
    visible,
    firstName,
    middleName,
    lastName,
    setFirstName,
    setMiddleName,
    setLastName,
    dirtyFirst,
    setDirtyFirst,
    dirtyLast,
    setDirtyLast,
    dirtyEmail,
    setDirtyEmail,
    validFirst,
    validLast,
    validEmail,
    dob,
    setDob,
    minDob,
    maxDob,
    dobOpen,
    setDobOpen,
    gender,
    setGender,
    email,
    setEmail,
    calculateAge,
    CustomCalendar,
    singleNameField = false,
  } = props

  if (!visible) return null

  return (
    <>
      {/* Name row */}
      {singleNameField ? (
        <div className="flex flex-col gap-1.5 sm:gap-2">
          <Label htmlFor="fullName" className="text-sm sm:text-base">
            Name <span className="text-red-600">*</span>
          </Label>
          <Input
            id="fullName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={() => setDirtyFirst(true)}
            placeholder="Full name"
            aria-invalid={dirtyFirst && !validFirst}
            className={`text-sm sm:text-base h-9 sm:h-10 ${
              dirtyFirst && !validFirst
                ? 'border-red-500 focus-visible:ring-red-500'
                : ''
            }`}
          />
          {dirtyFirst && !validFirst && (
            <p className="text-[10px] sm:text-xs text-red-600">
              Name is required.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <div className="flex flex-col gap-1.5 sm:gap-2">
            <Label htmlFor="firstName" className="text-sm sm:text-base">
              First Name <span className="text-red-600">*</span>
            </Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={() => setDirtyFirst(true)}
              placeholder="John"
              aria-invalid={dirtyFirst && !validFirst}
              className={`text-sm sm:text-base h-9 sm:h-10 ${
                dirtyFirst && !validFirst
                  ? 'border-red-500 focus-visible:ring-red-500'
                  : ''
              }`}
            />
            {dirtyFirst && !validFirst && (
              <p className="text-[10px] sm:text-xs text-red-600">
                First name is required.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5 sm:gap-2">
            <Label htmlFor="middleName" className="text-sm sm:text-base">
              Middle Name
            </Label>
            <Input
              id="middleName"
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
              placeholder="Alexander (optional)"
              className="text-sm sm:text-base h-9 sm:h-10"
            />
          </div>

          <div className="flex flex-col gap-1.5 sm:gap-2">
            <Label htmlFor="lastName" className="text-sm sm:text-base">
              Last Name <span className="text-red-600">*</span>
            </Label>
            <Input
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onBlur={() => setDirtyLast(true)}
              placeholder="Doe"
              aria-invalid={dirtyLast && !validLast}
              className={`text-sm sm:text-base h-9 sm:h-10 ${
                dirtyLast && !validLast
                  ? 'border-red-500 focus-visible:ring-red-500'
                  : ''
              }`}
            />
            {dirtyLast && !validLast && (
              <p className="text-[10px] sm:text-xs text-red-600">
                Last name is required.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Gender + DOB */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div className="flex flex-col gap-1.5 sm:gap-2">
          <Label className="text-sm sm:text-base">
            Gender <span className="text-red-600">*</span>
          </Label>
          <Select value={gender} onValueChange={setGender}>
            <SelectTrigger className="w-full h-9 sm:h-10 text-sm sm:text-base">
              <SelectValue placeholder="Select your gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="other">Other</SelectItem>
              <SelectItem value="prefer-not-to-say">
                Prefer not to say
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5 sm:gap-2">
          <Label className="text-sm sm:text-base">
            Date of Birth <span className="text-red-600">*</span>{' '}
            {dob && (
              <span className="text-gray-500 text-[10px] sm:text-xs">
                (Age: {calculateAge(dob)})
              </span>
            )}
          </Label>
          <Popover open={dobOpen} onOpenChange={setDobOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="w-full h-9 sm:h-10 px-3 rounded-md border border-input bg-background text-left text-sm shadow-sm flex items-center justify-between"
                onClick={() => setDobOpen(true)}
              >
                <span className="text-sm sm:text-base truncate">
                  {dob
                    ? dob.toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'Select date of birth'}
                </span>
                <CalendarIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 opacity-70 flex-shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="p-2 w-[min(22rem,90vw)] sm:w-[24rem] max-w-none"
            >
              <CustomCalendar
                className="w-full"
                selected={dob ?? null}
                onChange={(d) => {
                  setDob(d)
                  setDobOpen(false)
                }}
                minDate={minDob}
                maxDate={maxDob}
              />
            </PopoverContent>
          </Popover>
          {dob && (dob < minDob || dob > maxDob) && (
            <p className="text-[10px] sm:text-xs text-red-600">
              Please pick a valid date (not in the future; not older than 120
              years).
            </p>
          )}
        </div>
      </div>

      {/* Email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div className="flex flex-col gap-1.5 sm:gap-2">
          <Label htmlFor="email" className="text-sm sm:text-base">
            Email Address
          </Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setDirtyEmail(true)}
            placeholder="john.doe@example.com (optional)"
            aria-invalid={dirtyEmail && !validEmail}
            className={`text-sm sm:text-base h-9 sm:h-10 ${
              dirtyEmail && !validEmail
                ? 'border-red-500 focus-visible:ring-red-500'
                : ''
            }`}
          />
          {dirtyEmail && !validEmail && (
            <p className="text-[10px] sm:text-xs text-red-600">
              Enter a valid email or leave it blank.
            </p>
          )}
        </div>
      </div>
    </>
  )
}

export default PatientDetailsForm
